import { createReference, getReferenceString } from '@medplum/core';
import { Patient } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { canSearchResourceType } from '@/services/permissions';
import { useSubscriptionWithLimitHandling } from '@/services/subscriptions';
import { updateInsuranceCoverage } from '@/components/Timeline/TimelineItems/PatientAccessCall/insuranceVerification';
import type { UpdateInsuranceCoverageInput } from '../../../../../../bots/src/handlers/update-insurance-coverage';
import { InsuranceSectionRead, readInsuranceSection } from './insuranceDetails';

export type InsuranceSectionState = InsuranceSectionRead | { status: 'loading' };

/** The fields one save changes; left out is untouched, `null` clears. */
export type InsuranceEdit = Omit<UpdateInsuranceCoverageInput, 'patient'>;

export interface InsuranceSection {
  state: InsuranceSectionState;
  /**
   * Writes the fields through `update-insurance-coverage` (which records the Provenance), then
   * reads the card again, so the row shows the saved value once this resolves. Throws when the
   * bot refuses or fails.
   */
  save: (edit: InsuranceEdit) => Promise<void>;
}

const LOADING: InsuranceSectionState = { status: 'loading' };

/**
 * The patient's insurance card for the Insurance section, read while `enabled`. Nothing is read
 * while it is off, so a flag that is off costs no Coverage search.
 *
 * Read again whenever one of the patient's Coverages changes: the PN card on the same page saves
 * to this Coverage through the same bot, and bariendo-be creates it on a switch to the insurance
 * track.
 */
export function useInsuranceSection(patient: Patient, enabled: boolean): InsuranceSection {
  const medplum = useMedplum();
  // Kept with the patient it was read for: the page does not remount on a patient switch, and the
  // previous patient's card must not be drawn under the next one while their read is in flight.
  const [read, setRead] = useState<{ patientId: string; state: InsuranceSectionRead } | undefined>(undefined);
  const patientId = patient.id;
  // Read once per patient and change: the Patient object changes on every live update.
  const patientRef = useRef(patient);
  patientRef.current = patient;
  // Only the newest read lands: a read started before a save must not overwrite what it saved.
  const lastRead = useRef(0);

  const readNow = useCallback(async (): Promise<void> => {
    const target = patientRef.current;
    if (!target.id) return;
    const sequence = ++lastRead.current;
    const state = await readInsuranceSection(medplum, target);
    if (sequence === lastRead.current && target.id === patientRef.current.id) {
      setRead({ patientId: target.id, state });
    }
  }, [medplum]);

  const [changes, countChange] = useReducer((n: number) => n + 1, 0);
  const beneficiary = patientId ? getReferenceString(patientRef.current) : undefined;
  useSubscriptionWithLimitHandling(
    enabled && beneficiary && canSearchResourceType(medplum, 'Coverage')
      ? `Coverage?${new URLSearchParams({ beneficiary })}`
      : undefined,
    countChange
  );

  useEffect(() => {
    if (!enabled || !patientId) return;
    void readNow();
  }, [readNow, enabled, patientId, changes]);

  const save = useCallback(
    async (edit: InsuranceEdit): Promise<void> => {
      await updateInsuranceCoverage(medplum, { patient: createReference(patientRef.current), ...edit });
      await readNow();
    },
    [medplum, readNow]
  );

  return { state: read && read.patientId === patientId ? read.state : LOADING, save };
}
